/**
 * AccreditationEvidence.jsx
 *
 * Requirement-mapped evidence management page for a single course accreditation.
 *
 * Layout:
 *   - Page header with course name + accreditation reference
 *   - Completion score bar + KPI row
 *   - Two-column: left = requirement category list, right = evidence items for selected category
 *   - Upload, Replace, Delete, Download per item
 *   - Admin review status visible inline
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader, KPIGrid, StandardModal, Alert, Btn,
  StatusBadge, Card,
  inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius, shadows,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const EV_API = `${BASE}/evidence-categories`;

const token = () => localStorage.getItem("token");
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_COLORS = {
  submitted: colors.warning,
  under_review: "#7c3aed",
  approved: colors.success,
  rejected: colors.danger,
  requires_clarification: "#f59e0b",
};

const STATUS_LABELS = {
  submitted: "Pending Review",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  requires_clarification: "Clarification Required",
};

// ---------------------------------------------------------------------------
// Score bar
// ---------------------------------------------------------------------------

function ScoreBar({ score, status }) {
  const barColor =
    score === 100 ? colors.success :
    score >= 80   ? "#3b82f6" :
    score >= 60   ? colors.warning :
    colors.danger;

  const statusLabel = {
    complete:              "Complete",
    substantially_complete:"Substantially Complete",
    incomplete:            "Incomplete — review paused pending evidence",
    materially_incomplete: "Materially Incomplete — review not commenced",
  }[status] || status;

  return (
    <div style={{ marginBottom: spacing[5] }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: spacing[1],
      }}>
        <span style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary }}>
          Evidence Completion
        </span>
        <span style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: barColor }}>
          {score}% — {statusLabel}
        </span>
      </div>
      <div style={{
        height: 8, borderRadius: radius.full, background: colors.borderLight,
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%", width: `${score}%`,
          background: barColor,
          borderRadius: radius.full,
          transition: "width 0.4s ease",
        }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category list item (left sidebar)
// ---------------------------------------------------------------------------

function CategoryRow({ cat, selected, onClick }) {
  const complete = cat.has_evidence;
  const required = cat.required;

  let indicator = colors.borderLight;
  let indicatorLabel = "No evidence";
  if (complete) { indicator = colors.success; indicatorLabel = "Complete"; }
  else if (required) { indicator = colors.danger; indicatorLabel = "Required — missing"; }

  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: spacing[3],
        width: "100%", textAlign: "left",
        padding: `${spacing[3]} ${spacing[4]}`,
        background: selected ? colors.primary + "12" : "transparent",
        borderLeft: selected ? `3px solid ${colors.primary}` : "3px solid transparent",
        border: "none",
        borderBottom: `1px solid ${colors.borderLight}`,
        cursor: "pointer",
        transition: "background 0.15s",
      }}
    >
      {/* Status dot */}
      <div style={{
        width: 10, height: 10, borderRadius: "50%",
        background: indicator,
        flexShrink: 0,
        marginTop: 5,
      }} title={indicatorLabel} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: typography.fontSizes.xs,
          color: colors.textMuted,
          fontFamily: "monospace",
          marginBottom: 2,
        }}>
          {cat.code}
          {required && (
            <span style={{
              marginLeft: spacing[1],
              padding: "1px 5px",
              borderRadius: radius.sm,
              background: colors.danger + "18",
              color: colors.danger,
              fontSize: 10,
              fontFamily: typography.fontFamily,
            }}>Required</span>
          )}
        </div>
        <div style={{
          fontSize: typography.fontSizes.sm,
          fontWeight: selected ? typography.fontWeights.semibold : typography.fontWeights.normal,
          color: colors.textPrimary,
          lineHeight: 1.3,
        }}>
          {cat.name}
        </div>
        {cat.items && cat.items.length > 0 && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
            {cat.items.length} file{cat.items.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Evidence item row
// ---------------------------------------------------------------------------

function EvidenceItem({ item, onDownload, onReplace, onDelete, onReview, isAdmin }) {
  const statusColor = STATUS_COLORS[item.status] || colors.textMuted;
  const statusLabel = STATUS_LABELS[item.status] || item.status;

  return (
    <div style={{
      padding: `${spacing[3]} ${spacing[4]}`,
      borderBottom: `1px solid ${colors.borderLight}`,
      display: "flex", alignItems: "flex-start", gap: spacing[4],
    }}>
      {/* File icon / type */}
      <div style={{
        width: 36, height: 36, borderRadius: radius.sm,
        background: colors.bgSubtle,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        fontSize: typography.fontSizes.xs,
        fontWeight: typography.fontWeights.semibold,
        color: colors.textSecondary,
        fontFamily: "monospace",
        textTransform: "uppercase",
      }}>
        {(item.file_type || "file").slice(0, 4)}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: typography.fontWeights.semibold,
          fontSize: typography.fontSizes.sm,
          color: colors.textPrimary,
          marginBottom: 2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {item.title}
        </div>
        <div style={{
          fontSize: typography.fontSizes.xs, color: colors.textMuted,
          display: "flex", gap: spacing[3], flexWrap: "wrap",
        }}>
          <span>{item.file_name || "—"}</span>
          {item.file_size && <span>{formatBytes(item.file_size)}</span>}
          <span>v{item.version}</span>
          <span>Uploaded {formatDate(item.submitted_at)}</span>
        </div>
        {item.description && (
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textSecondary, marginTop: 4 }}>
            {item.description}
          </div>
        )}
        {item.reviewer_comments && (
          <div style={{
            marginTop: spacing[2], fontSize: typography.fontSizes.xs,
            padding: `${spacing[2]} ${spacing[3]}`,
            background: statusColor + "12",
            borderLeft: `2px solid ${statusColor}`,
            borderRadius: radius.sm,
            color: colors.textPrimary,
          }}>
            <span style={{ fontWeight: typography.fontWeights.semibold }}>Reviewer: </span>
            {item.reviewer_comments}
          </div>
        )}
      </div>

      {/* Status + actions */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: spacing[2], flexShrink: 0 }}>
        <span style={{
          fontSize: typography.fontSizes.xs,
          padding: "2px 8px",
          borderRadius: radius.full,
          background: statusColor + "18",
          color: statusColor,
          fontWeight: typography.fontWeights.semibold,
          whiteSpace: "nowrap",
        }}>
          {statusLabel}
        </span>
        <div style={{ display: "flex", gap: spacing[1] }}>
          <Btn size="sm" variant="ghost" onClick={() => onDownload(item)}>Download</Btn>
          <Btn size="sm" variant="ghost" onClick={() => onReplace(item)}>Replace</Btn>
          <Btn size="sm" variant="ghost" onClick={() => onDelete(item)}>Delete</Btn>
          {isAdmin && item.status === "submitted" && (
            <Btn size="sm" variant="primary" onClick={() => onReview(item)}>Review</Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function AccreditationEvidence() {
  const { accredId } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [scoreData, setScoreData]   = useState(null);
  const [missing, setMissing]       = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Modals
  const [showUpload, setShowUpload]   = useState(false);
  const [showReplace, setShowReplace] = useState(null);  // evidence item
  const [showDelete, setShowDelete]   = useState(null);  // evidence item
  const [showReview, setShowReview]   = useState(null);  // evidence item (admin)

  const [uploadForm, setUploadForm] = useState({ title: "", description: "", category: "" });
  const [reviewForm, setReviewForm] = useState({ status: "approved", comments: "" });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [msgType, setMsgType] = useState("success");

  const uploadFileRef  = useRef();
  const replaceFileRef = useRef();

  const userRole  = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isAdmin   = userRole === "super_admin" || userRole === "third_party_admin";

  const flash = (m, t = "success") => {
    setMsg(m); setMsgType(t);
    setTimeout(() => setMsg(""), 3500);
  };

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, scoreRes, missRes] = await Promise.all([
        fetch(`${EV_API}/accreditation/${accredId}/by-category`, { headers: authHeaders() }),
        fetch(`${EV_API}/accreditation/${accredId}/completion-score`, { headers: authHeaders() }),
        fetch(`${EV_API}/accreditation/${accredId}/missing`, { headers: authHeaders() }),
      ]);

      if (catRes.ok)   setCategories(await catRes.json());
      if (scoreRes.ok) setScoreData(await scoreRes.json());
      if (missRes.ok)  { const d = await missRes.json(); setMissing(d.missing || []); }
    } catch {
      flash("Failed to load evidence data.", "error");
    } finally {
      setLoading(false);
    }
  }, [accredId]);

  useEffect(() => { load(); }, [load]);

  // Auto-select first required missing category, else first category
  useEffect(() => {
    if (categories.length && !selectedCode) {
      const firstMissing = categories.find(c => c.required && !c.has_evidence);
      setSelectedCode((firstMissing || categories[0]).code);
    }
  }, [categories, selectedCode]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async function handleUpload(e) {
    e.preventDefault();
    const file = uploadFileRef.current?.files?.[0];
    if (!file) { flash("Please select a file.", "error"); return; }
    if (!uploadForm.category) { flash("Please select a requirement category.", "error"); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("requirement_category", uploadForm.category);
      fd.append("title", uploadForm.title);
      if (uploadForm.description) fd.append("description", uploadForm.description);
      fd.append("file", file);

      const res = await fetch(`${EV_API}/accreditation/${accredId}`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Upload failed"); }
      flash("Evidence uploaded.");
      setShowUpload(false);
      setUploadForm({ title: "", description: "", category: uploadForm.category });
      uploadFileRef.current.value = "";
      load();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReplace(e) {
    e.preventDefault();
    const file = replaceFileRef.current?.files?.[0];
    if (!file) { flash("Please select a replacement file.", "error"); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${EV_API}/${showReplace.id}/replace`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Replace failed"); }
      flash("Evidence replaced. Previous version archived.");
      setShowReplace(null);
      replaceFileRef.current.value = "";
      load();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!showDelete) return;
    setSaving(true);
    try {
      const res = await fetch(`${EV_API}/${showDelete.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Delete failed"); }
      flash("Evidence deleted.");
      setShowDelete(null);
      load();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReview(e) {
    e.preventDefault();
    if (!showReview) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("review_status", reviewForm.status);
      if (reviewForm.comments) fd.append("reviewer_comments", reviewForm.comments);

      const res = await fetch(`${EV_API}/${showReview.id}/review`, {
        method: "PATCH", headers: authHeaders(), body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Review failed"); }
      flash("Review recorded.");
      setShowReview(null);
      setReviewForm({ status: "approved", comments: "" });
      load();
    } catch (err) {
      flash(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  function handleDownload(item) {
    const link = document.createElement("a");
    link.href = `${EV_API}/${item.id}/download`;
    link.setAttribute("download", item.file_name || "evidence");
    // Pass auth token via header is not possible with plain anchor; open in new tab
    // For proper auth download, redirect with token in query (if backend supports) or use fetch+blob
    fetch(`${EV_API}/${item.id}/download`, { headers: authHeaders() })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(() => flash("Download failed.", "error"));
  }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const selectedCat = categories.find(c => c.code === selectedCode);

  const kpis = scoreData ? [
    {
      label: "Completion Score",
      value: `${scoreData.score}%`,
      color: scoreData.score === 100 ? colors.success : scoreData.score >= 80 ? "#3b82f6" : colors.warning,
    },
    {
      label: "Required Complete",
      value: scoreData.complete_required_categories?.length ?? "—",
      color: colors.success,
    },
    {
      label: "Required Missing",
      value: scoreData.incomplete_required_categories?.length ?? "—",
      color: scoreData.incomplete_required_categories?.length > 0 ? colors.danger : colors.success,
    },
    {
      label: "Optional Complete",
      value: scoreData.complete_optional_categories?.length ?? "—",
      color: colors.textSecondary,
    },
  ] : [];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: `${spacing[6]} ${spacing[5]}` }}>
      {/* Header */}
      <PageHeader
        title="Evidence"
        subtitle={`Accreditation #${accredId} — Requirement-mapped evidence`}
        actions={
          <div style={{ display: "flex", gap: spacing[2] }}>
            <Btn variant="secondary" size="sm" onClick={() => navigate(-1)}>Back</Btn>
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                setUploadForm({ title: "", description: "", category: selectedCode || "" });
                setShowUpload(true);
              }}
            >
              Upload Evidence
            </Btn>
          </div>
        }
      />

      {/* Flash */}
      {msg && (
        <div style={{ marginBottom: spacing[4] }}>
          <Alert type={msgType === "error" ? "error" : "success"} message={msg} />
        </div>
      )}

      {/* Score bar */}
      {scoreData && <ScoreBar score={scoreData.score} status={scoreData.status} />}

      {/* KPI row */}
      {kpis.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: spacing[4],
          marginBottom: spacing[5],
        }}>
          {kpis.map(k => (
            <div key={k.label} style={{
              background: colors.surface,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.md,
              padding: `${spacing[4]} ${spacing[5]}`,
            }}>
              <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginBottom: spacing[1] }}>
                {k.label}
              </div>
              <div style={{
                fontSize: typography.fontSizes["2xl"],
                fontWeight: typography.fontWeights.bold,
                color: k.color,
                lineHeight: 1,
              }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Missing evidence warning */}
      {missing.length > 0 && (
        <div style={{ marginBottom: spacing[4] }}>
          <Alert
            type="warning"
            message={`${missing.length} required evidence ${missing.length === 1 ? "category is" : "categories are"} missing: ${missing.map(c => `${c.code} — ${c.name}`).join("; ")}.`}
          />
        </div>
      )}

      {/* Two-column layout */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        gap: spacing[5],
        alignItems: "start",
      }}>
        {/* Left: category list */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.borderLight}`,
          borderRadius: radius.md,
          overflow: "hidden",
        }}>
          <div style={{
            padding: `${spacing[3]} ${spacing[4]}`,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: typography.fontSizes.xs,
            fontWeight: typography.fontWeights.semibold,
            color: colors.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}>
            Requirements
          </div>
          {loading
            ? <div style={{ padding: spacing[5], color: colors.textMuted, fontSize: typography.fontSizes.sm }}>Loading…</div>
            : categories.map(cat => (
              <CategoryRow
                key={cat.code}
                cat={cat}
                selected={selectedCode === cat.code}
                onClick={() => setSelectedCode(cat.code)}
              />
            ))
          }
        </div>

        {/* Right: evidence panel */}
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.borderLight}`,
          borderRadius: radius.md,
          overflow: "hidden",
          minHeight: 400,
        }}>
          {!selectedCat ? (
            <div style={{ padding: spacing[8], textAlign: "center", color: colors.textMuted }}>
              Select a requirement category on the left.
            </div>
          ) : (
            <>
              {/* Category header */}
              <div style={{
                padding: `${spacing[4]} ${spacing[5]}`,
                borderBottom: `1px solid ${colors.borderLight}`,
                background: colors.bgSubtle,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: spacing[4] }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[1] }}>
                      <span style={{
                        fontSize: typography.fontSizes.xs,
                        fontFamily: "monospace",
                        fontWeight: typography.fontWeights.semibold,
                        color: colors.textMuted,
                      }}>
                        {selectedCat.code}
                      </span>
                      {selectedCat.required && (
                        <span style={{
                          padding: "1px 7px", borderRadius: radius.full,
                          background: colors.danger + "18", color: colors.danger,
                          fontSize: 10, fontWeight: typography.fontWeights.semibold,
                        }}>Required</span>
                      )}
                      {selectedCat.has_evidence && (
                        <span style={{
                          padding: "1px 7px", borderRadius: radius.full,
                          background: colors.success + "18", color: colors.success,
                          fontSize: 10, fontWeight: typography.fontWeights.semibold,
                        }}>Complete</span>
                      )}
                    </div>
                    <div style={{ fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, fontSize: typography.fontSizes.base }}>
                      {selectedCat.name}
                    </div>
                    <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, marginTop: spacing[1] }}>
                      {selectedCat.description}
                    </div>
                    <div style={{
                      marginTop: spacing[2],
                      fontSize: typography.fontSizes.xs, color: colors.textMuted,
                      padding: `${spacing[2]} ${spacing[3]}`,
                      background: colors.surface,
                      border: `1px solid ${colors.borderLight}`,
                      borderRadius: radius.sm,
                    }}>
                      <span style={{ fontWeight: typography.fontWeights.semibold }}>Examples: </span>
                      {selectedCat.examples}
                    </div>
                  </div>
                  <Btn
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setUploadForm({ title: "", description: "", category: selectedCat.code });
                      setShowUpload(true);
                    }}
                  >
                    Upload
                  </Btn>
                </div>
              </div>

              {/* Evidence items */}
              {!selectedCat.items || selectedCat.items.length === 0 ? (
                <div style={{
                  padding: `${spacing[8]} ${spacing[5]}`,
                  textAlign: "center",
                  color: colors.textMuted,
                  fontSize: typography.fontSizes.sm,
                }}>
                  {selectedCat.required
                    ? "No evidence uploaded. This category is required before the application can be submitted to the decision panel."
                    : "No evidence uploaded for this category."}
                  <div style={{ marginTop: spacing[3] }}>
                    <Btn
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setUploadForm({ title: "", description: "", category: selectedCat.code });
                        setShowUpload(true);
                      }}
                    >
                      Upload Evidence
                    </Btn>
                  </div>
                </div>
              ) : (
                selectedCat.items.map(item => (
                  <EvidenceItem
                    key={item.id}
                    item={item}
                    isAdmin={isAdmin}
                    onDownload={handleDownload}
                    onReplace={ev => setShowReplace(ev)}
                    onDelete={ev => setShowDelete(ev)}
                    onReview={ev => { setShowReview(ev); setReviewForm({ status: "approved", comments: "" }); }}
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Upload modal                                                        */}
      {/* ------------------------------------------------------------------ */}
      {showUpload && (
        <StandardModal
          title="Upload Evidence"
          onClose={() => setShowUpload(false)}
          width={520}
          footer={
            <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowUpload(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="upload-form" disabled={saving}>
                {saving ? "Uploading…" : "Upload"}
              </Btn>
            </div>
          }
        >
          <form id="upload-form" onSubmit={handleUpload} style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            {/* Category */}
            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                Requirement Category <span style={{ color: colors.danger }}>*</span>
              </label>
              <select
                required
                value={uploadForm.category}
                onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                style={selectStyle}
              >
                <option value="">Select category…</option>
                {categories
                  .filter(c => c.code !== "UNCATEGORISED")
                  .map(c => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}{c.required ? " (Required)" : ""}
                    </option>
                  ))}
              </select>

              {/* Show description for selected category */}
              {uploadForm.category && CATEGORY_HINT[uploadForm.category] && (
                <div style={{
                  marginTop: spacing[2],
                  fontSize: typography.fontSizes.xs, color: colors.textSecondary,
                  padding: `${spacing[2]} ${spacing[3]}`,
                  background: colors.bgSubtle,
                  borderRadius: radius.sm,
                  borderLeft: `2px solid ${colors.primary}`,
                }}>
                  <div style={{ fontWeight: typography.fontWeights.semibold, marginBottom: 2 }}>What to upload:</div>
                  {CATEGORY_HINT[uploadForm.category].description}
                  <div style={{ marginTop: 4 }}>
                    <span style={{ fontWeight: typography.fontWeights.semibold }}>Examples: </span>
                    {CATEGORY_HINT[uploadForm.category].examples}
                  </div>
                </div>
              )}
            </div>

            {/* Title */}
            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                Document Title <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                required
                value={uploadForm.title}
                onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Trainer CV — Jane Smith"
                style={inputStyle}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                Description <span style={{ color: colors.textMuted, fontWeight: typography.fontWeights.normal }}>(optional)</span>
              </label>
              <textarea
                value={uploadForm.description}
                onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of what this document demonstrates…"
                rows={2}
                style={textareaStyle}
              />
            </div>

            {/* File */}
            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                File <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                ref={uploadFileRef}
                type="file"
                required
                accept=".pdf,.docx,.xlsx,.doc,.png,.jpg,.jpeg,.mp4"
                style={inputStyle}
              />
              <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: spacing[1] }}>
                Accepted: PDF, DOCX, XLSX, DOC, PNG, JPG, JPEG, MP4 — max 50 MB
              </div>
            </div>
          </form>
        </StandardModal>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Replace modal                                                       */}
      {/* ------------------------------------------------------------------ */}
      {showReplace && (
        <StandardModal
          title="Replace Evidence"
          onClose={() => setShowReplace(null)}
          width={480}
          footer={
            <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowReplace(null)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="replace-form" disabled={saving}>
                {saving ? "Uploading…" : "Replace"}
              </Btn>
            </div>
          }
        >
          <div style={{ marginBottom: spacing[4] }}>
            <Alert
              type="info"
              message={`Replacing "${showReplace.title}" (v${showReplace.version}). The current file will be archived and remain accessible in version history.`}
            />
          </div>
          <form id="replace-form" onSubmit={handleReplace} style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                New File <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                ref={replaceFileRef}
                type="file"
                required
                accept=".pdf,.docx,.xlsx,.doc,.png,.jpg,.jpeg,.mp4"
                style={inputStyle}
              />
            </div>
          </form>
        </StandardModal>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Delete confirmation modal                                           */}
      {/* ------------------------------------------------------------------ */}
      {showDelete && (
        <StandardModal
          title="Delete Evidence"
          onClose={() => setShowDelete(null)}
          width={440}
          danger
          footer={
            <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowDelete(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDelete} disabled={saving}>
                {saving ? "Deleting…" : "Delete"}
              </Btn>
            </div>
          }
        >
          <p style={{ margin: 0, color: colors.textPrimary, fontSize: typography.fontSizes.sm }}>
            Delete <strong>{showDelete.title}</strong> (v{showDelete.version})?
            The file will be removed from the evidence list. This action is recorded in the audit trail and cannot be undone.
          </p>
        </StandardModal>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Review modal (admin only)                                           */}
      {/* ------------------------------------------------------------------ */}
      {showReview && isAdmin && (
        <StandardModal
          title="Review Evidence"
          onClose={() => setShowReview(null)}
          width={500}
          footer={
            <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowReview(null)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="review-form" disabled={saving}>
                {saving ? "Saving…" : "Submit Review"}
              </Btn>
            </div>
          }
        >
          <form id="review-form" onSubmit={handleReview} style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <div style={{
              padding: `${spacing[3]} ${spacing[4]}`,
              background: colors.bgSubtle,
              borderRadius: radius.sm,
              fontSize: typography.fontSizes.sm,
            }}>
              <div style={{ fontWeight: typography.fontWeights.semibold }}>{showReview.title}</div>
              <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.xs, marginTop: 2 }}>
                {showReview.file_name} · v{showReview.version} · {formatDate(showReview.submitted_at)}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                Decision <span style={{ color: colors.danger }}>*</span>
              </label>
              <select
                required
                value={reviewForm.status}
                onChange={e => setReviewForm(f => ({ ...f, status: e.target.value }))}
                style={selectStyle}
              >
                <option value="approved">Approved — evidence meets requirements</option>
                <option value="rejected">Rejected — evidence does not meet requirements</option>
                <option value="requires_clarification">Requires Clarification — further information needed</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing[1] }}>
                Comments
                {reviewForm.status !== "approved" && <span style={{ color: colors.danger }}> *</span>}
              </label>
              <textarea
                required={reviewForm.status !== "approved"}
                value={reviewForm.comments}
                onChange={e => setReviewForm(f => ({ ...f, comments: e.target.value }))}
                placeholder={
                  reviewForm.status === "rejected" ? "State specifically which requirement is not met and what is needed…" :
                  reviewForm.status === "requires_clarification" ? "State what additional information or evidence is required…" :
                  "Optional notes on why this evidence has been approved…"
                }
                rows={3}
                style={textareaStyle}
              />
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}

// Category hint lookup for upload modal (mirrors backend definitions)
const CATEGORY_HINT = {
  "EV-01": { description: "Full course or qualification specification with learning outcomes and syllabus.", examples: "Course specification document, unit descriptors, learning outcomes framework." },
  "EV-02": { description: "All assessment instruments: tasks, mark schemes, observation checklists.", examples: "Assessment task briefs, marking criteria, observation record templates." },
  "EV-03": { description: "Provider's documented approach to planning and administering assessment.", examples: "Assessment strategy document, assessment plan, scheduling procedure." },
  "EV-04": { description: "CVs and qualification certificates for all trainers delivering this course.", examples: "CV, teaching qualification certificate (min. Level 3 AET or equivalent), occupational experience record." },
  "EV-05": { description: "CVs and qualification certificates for all assessors assessing this course.", examples: "CV, assessing qualification (D32/D33, A1, TAQA L3/4, or equivalent), occupational experience." },
  "EV-06": { description: "Internal Quality Assurance strategy, sampling records, and standardisation records.", examples: "IQA strategy document, sample records, standardisation meeting minutes." },
  "EV-07": { description: "CPD logs demonstrating continuing professional development for trainers and assessors.", examples: "CPD log for current year, attendance certificates, reflection records." },
  "EV-08": { description: "Current learner-facing guidance document covering the course, assessment, and support.", examples: "Learner handbook, student guide, learner induction pack." },
  "EV-09": { description: "Malpractice & maladministration, data protection, reasonable adjustments, and complaints policies.", examples: "Malpractice policy, GDPR/data protection policy, reasonable adjustments policy, complaints procedure." },
  "EV-10": { description: "Provider structure, staffing, and financial stability evidence.", examples: "Organisational chart, company registration, financial statements or bank reference." },
  "EV-11": { description: "Evidence of an appropriate delivery environment meeting health, safety, and accessibility requirements.", examples: "Premises photos, H&S policy, room capacity and layout details." },
  "EV-12": { description: "Equality and diversity policy and evidence of implementation.", examples: "E&D policy, equality impact assessment, staff or learner monitoring data." },
  "EV-13": { description: "Anonymised samples of assessed learner work at pass, borderline, and fail.", examples: "Anonymised learner portfolios or assignments with assessor decisions and feedback." },
  "EV-14": { description: "External verifier or examiner reports from previous delivery cycles.", examples: "EV/EQA visit report, external examiner feedback." },
  "EV-15": { description: "Previous accreditation certificates, conditions, or monitoring reports.", examples: "Prior accreditation certificate, conditions letter, surveillance report." },
};
